import React from 'react';
import { graphql, Link } from 'gatsby';
import styled from '@emotion/styled';
import { Inner } from 'styles/structure';
import colors from 'styles/colors';
import Content from 'components/_ui/Content';
import dimensions from 'styles/dimensions';
import Layout from 'components/Layout';
import { Rubric } from 'styles/typography';
import Button from 'components/_ui/Button';
import Linkedin from '../../vectors/linkedin.svg';
import Twitter from '../../vectors/twitter.svg';
import Facebook from '../../vectors/facebook.svg';
import SEO from 'components/SEO';

const PersonHero = styled.div`
  background-color: ${colors.orange100};
  padding: 4rem 0;

  @media (min-width: ${dimensions.tabletLandscapeUp}px) {
    padding-top: 5.6rem;
    padding-bottom: 8rem;
  }
`;

const HeroInner = styled(Inner)`
  max-width: 77.6rem;

  @media (min-width: ${dimensions.tabletLandscapeUp}px) {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    max-width: 77.6rem;
  }
`;

const HeroImage = styled.div`
  position: relative;
  max-width: 33.6rem;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    padding-bottom: 100%;
  }

  img {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  & + * {
    margin-top: 2.4rem;
  }

  @media (min-width: ${dimensions.tabletLandscapeUp}px) {
    flex: 1 1 33.6rem;
    margin-right: 6.4rem;
  }
`;

const HeroContent = styled.div``;

const HeroSocial = styled.ul`
  li {
    display: inline-block;

    & + * {
      margin-left: 2.2rem;
    }
  }

  a {
    padding: 0.4rem;

    &:hover {
      svg {
        fill: ${colors.red500};
      }
    }
  }

  svg {
    fill: ${colors.teal100};
    width: 2.4rem;
    height: 2.4rem;
    margin-top: 2.4rem;
    transition: fill 0.08s ease-in-out;
  }

  @media (min-width: ${dimensions.tabletLandscapeUp}px) {
    svg {
      width: 2rem;
      height: 2rem;
    }
  }
`;

const PersonInner = styled(Inner)`
  max-width: 77.6rem;

  @media (min-width: ${dimensions.tabletLandscapeUp}px) {
    max-width: 77.6rem;
  }
`;

const PersonContent = styled.div`
  margin: 6.4rem 0;

  @media (min-width: ${dimensions.tabletLandscapeUp}px) {
    margin: 7.2rem 0 10.4rem;
  }
`;

const PersonButtons = styled.div`
  margin-top: 6.4rem;
  
  > * {
    display: block;
    
    & + * {
      margin-top: 1.6rem;
    }
  }
  
  @media (min-width: ${dimensions.tabletLandscapeUp}px) {
    > * {
      display: inline-block;
        
      & + * {
        margin-top: 0;
        margin-left: 1.6rem;
      }
    }
  }
`;

export default ({ data }) => {
  const bio = data.prismic.bioByUID;
  const membersBoard = data.prismic.allAbout_uss.edges[0].node.board_members;
  const membersManagement = data.prismic.allAbout_uss.edges[0].node.management_members;

  if (!bio) {
    return null;
  }

  return (
    <Layout>
      <SEO
        title={bio.name}
        description={bio.social_description ? bio.social_description : null}
        image={bio.image ? bio.image.url : null}
      />

      <PersonHero>
        <HeroInner>
          <HeroImage>
            {bio.image && <img src={bio.image.url} alt={bio.image.alt} loading="lazy" />}
          </HeroImage>

          <HeroContent>
            <h2>{bio.name}</h2>

            {bio.title && <Rubric>{bio.title}</Rubric>}

            <HeroSocial>
              {bio.facebook && (
                <li>
                  <a
                    href={`https://www.facebook.com/${bio.facebook}`}
                    target='_blank'
                    rel="noopener noreferrer"
                  >
                    <Facebook />
                  </a>
                </li>
              )}

              {bio.twitter && (
                <li>
                  <a
                    href={`https://www.twitter.com/${bio.twitter}`}
                    target='_blank'
                    rel="noopener noreferrer"
                  >
                    <Twitter />
                  </a>
                </li>
              )}

              {bio.linkedin && (
                <li>
                  <a
                    href={`https://www.linkedin.com/in/${bio.linkedin}`}
                    target='_blank'
                    rel="noopener noreferrer"
                  >
                    <Linkedin />
                  </a>
                </li>
              )}
            </HeroSocial>
          </HeroContent>
        </HeroInner>
      </PersonHero>

      <PersonContent>
        <PersonInner>
          <Content content={bio.description} />

          <PersonButtons>
            {membersBoard.some(person => person.member && person.member.name === bio.name) && (
              <Button as={Link} to="/about/#board">
                Meet the Board
              </Button>
            )}

            {membersManagement.some(person => person.member && person.member.name === bio.name) && (
              <Button as={Link} to="/about/#management">
                Meet the Management Team
              </Button>
            )}
          </PersonButtons>
        </PersonInner>
      </PersonContent>
    </Layout>
  );
};

export const query = graphql`
  query getBio($uid: String!) {
    prismic {
      bioByUID: person(uid: $uid, lang: "en-us") {
        name
        title
        image
        description
        linkedin
        twitter
        facebook
      }
      allAbout_uss {
        edges {
          node {
            board_members {
              member {
                ... on PRISMIC_Person {
                  name
                  title
                  _meta {
                    uid
                  }
                }
              }
            }
            management_members {
              member {
                ... on PRISMIC_Person {
                  name
                  title
                  _meta {
                    uid
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
